import React, { useState, useEffect, useCallback } from 'react';
import {
  collection,
  onSnapshot,
  query,
  where,
  updateDoc,
  doc,
  deleteDoc,
  writeBatch,
  getDocs,
  Timestamp
} from 'firebase/firestore';
import {
  AlertTriangle,
  RefreshCw,
  LayoutDashboard,
  CalendarDays,
  Users,
  UserCheck,
  FileText,
  FileCheck2,
  Files,
  Menu,
  X
} from 'lucide-react';
import { rtdb } from '../firebase';
import { ref, onValue } from 'firebase/database';
import { useAdvancedPermissions } from '../hooks/useAdvancedPermissions';
import { useTheme } from '../contexts/ThemeContext';
import { BENEFICIARY_STATUS } from '../constants/beneficiaryStatuses';
import NFCModal from './NFCModal';
import ClientAuthManager from './ClientAuthManager';
import '../ResourceManagement.css';

// Import des modals externalisés
import ViewEmployeeModal from './modals/ViewEmployeeModal';
import EditEmployeeModal from './modals/EditEmployeeModal';
import ViewClientModal from './modals/ViewClientModal';
import EditClientModal from './modals/EditClientModal';
import AddClientModal from './modals/AddClientModal';
import ArchiveBeneficiaryModal from './modals/ArchiveBeneficiaryModal';
import ArchiveIntervenantModal from './modals/ArchiveIntervenantModal';
import InvoiceGeneratorModal from './modals/InvoiceGeneratorModal';
import ClientStatisticsModal from './modals/ClientStatisticsModal';
import ClientDocumentsPanel from './ClientDocumentsPanel';

// Import des composants séparés
import TableauDeBordModule from './modules/TableauDeBordModule';
import IntervenantsModule from './modules/IntervenantsModule';
import BeneficiairesModule from './modules/BeneficiairesModule';
import PlanningIntervenantsModule from './modules/PlanningIntervenantsModule';
import LegalDocumentsManager from './LegalDocumentsManager';
import ClientContractsManager from './ClientContractsManager';
import ClientContract from './ClientContract';
import GenericDocumentsManager from './GenericDocumentsManager';
import EmployeeAgendaView from './EmployeeAgendaView';

/**
 * Système complet de gestion des ressources avec intégration des données actuelles
 * Module qui récupère et affiche toutes les données des systèmes existants
 */
const ResourceManagementEnhanced = ({ db, currentUser }) => {
  const [moduleActif, setModuleActif] = useState('tableau-de-bord');
  const [menuMobileOuvert, setMenuMobileOuvert] = useState(false);
  const { theme } = useTheme();

  // États pour les données récupérées
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    totalClients: 0,
    activeEmployees: 0,
    activeClients: 0,
    totalAppointments: 0,
    upcomingAppointments: 0,
    totalContracts: 0,
    activeContracts: 0,
    expiringContracts: 0
  });

  // États pour les modals
  const [selectedResource, setSelectedResource] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNFCModal, setShowNFCModal] = useState(false);
  const [selectedResourceForNFC, setSelectedResourceForNFC] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedResourceForAuth, setSelectedResourceForAuth] = useState(null);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [selectedResourceForArchive, setSelectedResourceForArchive] = useState(null);
  const [resourceType, setResourceType] = useState(null); // 'intervenant' ou 'beneficiaire'
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedClientForInvoice, setSelectedClientForInvoice] = useState(null);
  const [showStatisticsModal, setShowStatisticsModal] = useState(false);
  const [selectedClientForStatistics, setSelectedClientForStatistics] = useState(null);
  const [showEmployeeAgendaModal, setShowEmployeeAgendaModal] = useState(false);
  const [selectedEmployeeForAgenda, setSelectedEmployeeForAgenda] = useState(null);
  const [showMandateModal, setShowMandateModal] = useState(false);
  const [selectedClientForMandate, setSelectedClientForMandate] = useState(null);
  const [onlineStatuses, setOnlineStatuses] = useState({});

  // Abonnement temps réel à la présence des intervenants (Firebase RTDB)
  useEffect(() => {
    const presenceRef = ref(rtdb, 'presence');
    const unsubscribe = onValue(presenceRef, (snapshot) => {
      const data = snapshot.val() || {};
      const statuses = {};
      Object.entries(data).forEach(([uid, val]) => {
        // Compatibilité objet { state: 'online' } ET booléen true/false
        statuses[uid] = val === true || val?.state === 'online';
      });
      setOnlineStatuses(statuses);
    });
    return () => unsubscribe();
  }, []);

  // Fonction pour fermer tous les modals
  const closeAllModals = () => {
    setShowViewModal(false);
    setShowEditModal(false);
    setShowNFCModal(false);
    setShowAuthModal(false);
    setShowAddClientModal(false);
    setShowInvoiceModal(false);
    setShowStatisticsModal(false);
    setShowMandateModal(false);
    setSelectedResource(null);
    setSelectedResourceForNFC(null);
    setSelectedResourceForAuth(null);
    setSelectedClientForInvoice(null);
    setSelectedClientForStatistics(null);
    setSelectedClientForMandate(null);
    setResourceType(null);
  };

  // Gestionnaire de clic en dehors des modals
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeAllModals();
    }
  };

  // Utiliser le nouveau système de permissions
  const { hasPermission, loading: permissionsLoading } = useAdvancedPermissions({
    userId: currentUser?.id,
    user: currentUser
  });

  // Modules disponibles
  const modules = [
    {
      id: 'tableau-de-bord',
      label: 'Tableau de bord',
      icone: LayoutDashboard,
      description: 'Vue d\'ensemble des ressources'
    },
    {
      id: 'planning',
      label: 'Planning',
      icone: CalendarDays,
      description: 'Planning consolidé des rendez-vous'
    },
    {
      id: 'intervenants',
      label: 'Intervenants',
      icone: Users,
      description: 'Gestion du personnel intervenant'
    },
    {
      id: 'beneficiaires',
      label: 'Bénéficiaires',
      icone: UserCheck,
      description: 'Gestion des bénéficiaires'
    },
    {
      id: 'documents-legaux',
      label: 'Documents Légaux',
      icone: FileText,
      description: 'Gestion des documents contractuels'
    },
    {
      id: 'contrats-clients',
      label: 'Contrats Clients',
      icone: FileCheck2,
      description: 'Gestion des contrats par bénéficiaire'
    },
    {
      id: 'documents-generiques',
      label: 'Documents Génériques',
      icone: Files,
      description: 'Documents uniques de l\'entreprise'
    }
  ];

  // Fonction pour mettre à jour les statistiques
  const updateStats = useCallback((emps, clis, apts, contractsData = []) => {
    const now = new Date();

    // Rendez-vous à venir
    const upcomingAppointments = apts.filter(apt => {
      if (apt.date && apt.startTime) {
        const aptDate = apt.date?.toDate ? apt.date.toDate() : new Date(apt.date);
        const [hours, minutes] = apt.startTime.split(':').map(Number);
        const appointmentDateTime = new Date(aptDate);
        appointmentDateTime.setHours(hours, minutes, 0, 0);
        return appointmentDateTime > now;
      }
      return false;
    });

    // Rendez-vous en cours
    const currentAppointments = apts.filter(apt => {
      if (apt.date && apt.startTime && apt.endTime) {
        const aptDate = apt.date?.toDate ? apt.date.toDate() : new Date(apt.date);
        const [startHours, startMinutes] = apt.startTime.split(':').map(Number);
        const [endHours, endMinutes] = apt.endTime.split(':').map(Number);

        const startDateTime = new Date(aptDate);
        startDateTime.setHours(startHours, startMinutes, 0, 0);

        const endDateTime = new Date(aptDate);
        endDateTime.setHours(endHours, endMinutes, 0, 0);

        return startDateTime <= now && endDateTime >= now;
      }
      return false;
    });

    // Statistiques des contrats
    const activeContracts = contractsData.filter(contract => contract.status === 'active');
    const expiringContracts = contractsData.filter(contract => {
      if (contract.status === 'active' && contract.endDate) {
        const endDate = new Date(contract.endDate);
        const diffTime = endDate - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= 30 && diffDays > 0;
      }
      return false;
    });

    // Statistiques avancées
    const totalHoursThisMonth = apts.reduce((total, apt) => {
      if (apt.startTime && apt.endTime) {
        const [startHours, startMinutes] = apt.startTime.split(':').map(Number);
        const [endHours, endMinutes] = apt.endTime.split(':').map(Number);
        const duration = (endHours * 60 + endMinutes) - (startHours * 60 + startMinutes);
        return total + (duration / 60); // Convertir en heures
      }
      return total;
    }, 0);

    setStats({
      totalEmployees: emps.length,
      totalClients: clis.length,
      activeEmployees: emps.filter(emp => emp.isActive !== false && !emp.isArchived).length,
      activeClients: clis.filter(cli => cli.isActive !== false).length,
      totalAppointments: apts.length,
      upcomingAppointments: upcomingAppointments.length,
      currentAppointments: currentAppointments.length,
      totalHoursThisMonth: Math.round(totalHoursThisMonth * 10) / 10,
      archivedEmployees: emps.filter(emp => emp.isArchived).length,
      inactiveClients: clis.filter(cli => cli.isActive === false).length,
      totalContracts: contractsData.length,
      activeContracts: activeContracts.length,
      expiringContracts: expiringContracts.length
    });

    setIsLoading(false);
  }, []);

  // Fonction pour obtenir le nom du rôle à partir de l'ID
  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : roleId; // Retourne le nom du rôle ou l'ID si non trouvé
  };

  // Écouter les changements en temps réel
  useEffect(() => {
    setIsLoading(true);

    let employeesData = [];
    let clientsData = [];
    let appointmentsData = [];
    let rolesData = [];
    let contractsData = [];
    let hasInitialData = false;

    // Timeout pour éviter que le chargement reste bloqué
    const loadingTimeout = setTimeout(() => {
      if (!hasInitialData) {
        setIsLoading(false);
      }
    }, 5000); // 5 secondes maximum

    // Écouter les rôles avancés
    const unsubscribeRoles = onSnapshot(
      collection(db, 'advanced_roles'),
      (snapshot) => {
        rolesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRoles(rolesData);
        hasInitialData = true;
      },
      (error) => {
        console.error('Erreur lors du chargement des rôles:', error);
      }
    );

    // Écouter les contrats clients
    const unsubscribeContracts = onSnapshot(
      collection(db, 'clientContracts'),
      (snapshot) => {
        contractsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setContracts(contractsData);
        hasInitialData = true;
        updateStats(employeesData, clientsData, appointmentsData, contractsData);
      },
      (error) => {
        console.error('Erreur lors du chargement des contrats:', error);
      }
    );

    const unsubscribeEmployees = onSnapshot(
      collection(db, 'employees'),
      (snapshot) => {
        employeesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          type: 'intervenant'
        }));
        setEmployees(employeesData);
        hasInitialData = true;
        updateStats(employeesData, clientsData, appointmentsData, contractsData);
      },
      (error) => {
        console.error('Erreur lors du chargement des employés:', error);
        setIsLoading(false);
      }
    );

    const unsubscribeClients = onSnapshot(
      collection(db, 'clients'),
      (snapshot) => {
        clientsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          type: 'beneficiaire'
        }));
        setClients(clientsData);
        hasInitialData = true;
        updateStats(employeesData, clientsData, appointmentsData, contractsData);
      },
      (error) => {
        console.error('Erreur lors du chargement des clients:', error);
        setIsLoading(false);
      }
    );

    const unsubscribeAppointments = onSnapshot(
      collection(db, 'appointments'),
      (snapshot) => {
        appointmentsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAppointments(appointmentsData);
        hasInitialData = true;
        updateStats(employeesData, clientsData, appointmentsData, contractsData);
      },
      (error) => {
        console.error('Erreur lors du chargement des rendez-vous:', error);
        setIsLoading(false);
      }
    );

    return () => {
      unsubscribeRoles();
      unsubscribeContracts();
      unsubscribeEmployees();
      unsubscribeClients();
      unsubscribeAppointments();
      clearTimeout(loadingTimeout);
    };
  }, [db, updateStats]);

  // Gestionnaire d'actions pour les ressources - Optimisé pour les performances
  const handleResourceAction = useCallback(async (action, resource) => {
    // Utiliser setTimeout pour décaler l'exécution et éviter les blocages
    setTimeout(async () => {
      // Sécurité: vérifier si la ressource est définie (sauf pour l'action refresh)
      if (!resource) {
        if (action === 'refresh') return;
        console.warn(`⚠️ Action "${action}" appelée sans ressource`);
        return;
      }

      const isIntervenant = resource.type === 'intervenant';

      switch (action) {
        case 'view':
          setSelectedResource(resource);
          setResourceType(isIntervenant ? 'intervenant' : 'beneficiaire');
          setShowViewModal(true);
          break;
        case 'edit':
          setSelectedResource(resource);
          setResourceType(isIntervenant ? 'intervenant' : 'beneficiaire');
          setShowEditModal(true);
          break;
        case 'delete':
          if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${resource.firstName} ${resource.lastName} ?`)) {
            try {
              await deleteDoc(doc(db, isIntervenant ? 'employees' : 'clients', resource.id));
              alert(`${resource.firstName} ${resource.lastName} a été supprimé avec succès`);
            } catch (error) {
              console.error('Erreur lors de la suppression:', error);
              alert('Erreur lors de la suppression');
            }
          }
          break;
        case 'archive':
          // Pour les bénéficiaires archivés, utiliser la désarchivation simple
          if (!isIntervenant && resource.isArchived) {
            // Désarchivation simple pour les bénéficiaires
            if (window.confirm(`Êtes-vous sûr de vouloir désarchiver ${resource.firstName} ${resource.lastName} ?`)) {
              try {
                await updateDoc(doc(db, 'clients', resource.id), {
                  isArchived: false,
                  // Conserver les informations d'archivage pour l'historique
                  archiveDate: null,
                  archiveReason: null
                });
                alert(`${resource.firstName} ${resource.lastName} a été désarchivé avec succès`);
              } catch (error) {
                console.error('Erreur lors de la désarchivation:', error);
                alert('Erreur lors de la désarchivation');
              }
            }
          } else if (!isIntervenant) {
            // Pour les bénéficiaires non archivés, utiliser le modal d'archivage avancé
            setSelectedResourceForArchive(resource);
            setShowArchiveModal(true);
          } else {
            // Pour les intervenants, utiliser le modal d'archivage avancé
            setSelectedResourceForArchive(resource);
            setShowArchiveModal(true);
          }
          break;
        case 'schedule':
          setSelectedEmployeeForAgenda(resource);
          setShowEmployeeAgendaModal(true);
          break;
        case 'contract':
          alert(`Générer un contrat pour ${resource.firstName} ${resource.lastName}`);
          break;
        case 'report':
          // Rediriger vers la page EmployeeReports avec l'intervenant sélectionné
          if (isIntervenant) {
            // Stocker l'intervenant sélectionné dans le localStorage pour EmployeeReports
            localStorage.setItem('selectedEmployeeForReports', JSON.stringify({
              id: resource.id,
              firstName: resource.firstName,
              lastName: resource.lastName,
              userId: resource.id // EmployeeReports utilise userId comme identifiant
            }));
            // Rediriger vers la page EmployeeReports
            window.location.href = '/dashboard/employeeReports';
          } else {
            alert(`Générer un rapport pour ${resource.firstName} ${resource.lastName}`);
          }
          break;
        case 'nfc':
          setSelectedResourceForNFC(resource);
          setShowNFCModal(true);
          break;
        case 'devis':
          alert(`Générer un devis pour ${resource.firstName} ${resource.lastName}`);
          break;
        case 'invoice':
          // Ouvrir le modal dédié à la génération de facture
          setSelectedClientForInvoice(resource);
          setShowInvoiceModal(true);
          break;
        case 'statistics':
          // Ouvrir le modal des statistiques complètes
          setSelectedClientForStatistics(resource);
          setShowStatisticsModal(true);
          break;
        case 'mandate':
          // Ouvrir le modal de gestion du contrat de mandat
          setSelectedClientForMandate(resource);
          setShowMandateModal(true);
          break;
        case 'auth':
          setSelectedResourceForAuth(resource);
          setShowAuthModal(true);
          break;
        case 'documents':
          setSelectedClientForDocuments(resource);
          setModuleActif('documents-legaux');
          break;
        case 'contracts':
          setSelectedClientForContracts(resource);
          setModuleActif('contrats-clients');
          break;
        case 'delete_appointments':
          if (window.confirm(`Êtes-vous sûr de vouloir supprimer tous les rendez-vous de ${resource.firstName} ${resource.lastName} ?`)) {
            try {
              const appointmentsRef = collection(db, 'appointments');
              const q = query(appointmentsRef, where('employeeId', '==', resource.id));
              const querySnapshot = await getDocs(q);

              const batch = writeBatch(db);
              querySnapshot.forEach((document) => {
                batch.delete(document.ref);
              });

              await batch.commit();
              alert('Tous les rendez-vous ont été supprimés avec succès');
            } catch (error) {
              console.error('Erreur lors de la suppression des rendez-vous:', error);
              alert('Erreur lors de la suppression des rendez-vous');
            }
          }
          break;
        case 'force_password_reset':
          if (window.confirm(`Forcer la réinitialisation du mot de passe pour ${resource.firstName} ${resource.lastName} ?`)) {
            try {
              await updateDoc(doc(db, 'employees', resource.id), {
                needPasswordReset: true
              });
              alert('Réinitialisation du mot de passe forcée avec succès');
            } catch (error) {
              console.error('Erreur lors de la réinitialisation:', error);
              alert('Erreur lors de la réinitialisation');
            }
          }
          break;
        case 'force_email_update':
          if (window.confirm(`Forcer la mise à jour de l\'email pour ${resource.firstName} ${resource.lastName} ?`)) {
            try {
              await updateDoc(doc(db, 'employees', resource.id), {
                needEmailUpdate: true
              });
              alert('Mise à jour de l\'email forcée avec succès');
            } catch (error) {
              console.error('Erreur lors de la mise à jour:', error);
              alert('Erreur lors de la mise à jour');
            }
          }
          break;
        case 'force_logout':
          if (window.confirm(`Forcer la déconnexion de ${resource.firstName} ${resource.lastName} ?`)) {
            try {
              // Logique de déconnexion forcée
              alert('Déconnexion forcée avec succès');
            } catch (error) {
              console.error('Erreur lors de la déconnexion:', error);
              alert('Erreur lors de la déconnexion');
            }
          }
          break;
        default:
          break;
      }
    }, 0); // Délai de 0ms pour décaler dans la file d'attente
  }, [db]);


  // États pour les filtres et la recherche des intervenants
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    status: 'active',
    role: 'all'
  });

  // Gestionnaire stable pour la recherche des intervenants
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  // Gestionnaire stable pour les filtres avancés des intervenants
  const handleAdvancedFiltersChange = useCallback((filterType, value) => {
    setAdvancedFilters(prev => ({ ...prev, [filterType]: value }));
  }, []);

  // Gestionnaire stable pour basculer les filtres avancés
  const toggleAdvancedFilters = useCallback(() => {
    setShowAdvancedFilters(prev => !prev);
  }, []);

  // Filtrer les employés selon les critères
  const filteredEmployees = employees.filter(employee => {
    // Exclure les comptes bénéficiaires (rôle 'beneficiaire')
    if (employee.role === 'beneficiaire') {
      return false;
    }

    // Filtre par recherche
    const matchesSearch = searchTerm === '' ||
      employee.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.role?.toLowerCase().includes(searchTerm.toLowerCase());

    // Filtre par statut (couvre actif, inactif, archivé et tous)
    const matchesStatus = (() => {
      switch (advancedFilters.status) {
        case 'active':   return employee.isActive !== false && !employee.isArchived;
        case 'inactive': return employee.isActive === false && !employee.isArchived;
        case 'archived': return employee.isArchived === true;
        default:         return true; // 'all'
      }
    })();

    // Filtre par rôle
    const matchesRole = advancedFilters.role === 'all' || employee.role === advancedFilters.role;

    return matchesSearch && matchesStatus && matchesRole;
  });

  // États pour les filtres des bénéficiaires
  const [searchTermClients, setSearchTermClients] = useState('');
  const [showAdvancedFiltersClients, setShowAdvancedFiltersClients] = useState(false);
  const [advancedFiltersClients, setAdvancedFiltersClients] = useState({
    status: 'active',
    typeEntite: 'all',
    hasEmail: 'all',
    hasPhone: 'all',
    hasAuthAccess: 'all'
  });

  // Gestionnaire stable pour la recherche des bénéficiaires
  const handleSearchClientsChange = useCallback((e) => {
    setSearchTermClients(e.target.value);
  }, []);

  // Gestionnaire stable pour les filtres avancés des bénéficiaires
  const handleAdvancedFiltersClientsChange = useCallback((filterType, value) => {
    setAdvancedFiltersClients(prev => ({ ...prev, [filterType]: value }));
  }, []);

  // Gestionnaire stable pour basculer les filtres avancés
  const toggleAdvancedFiltersClients = useCallback(() => {
    setShowAdvancedFiltersClients(prev => !prev);
  }, []);

  // Filtrer les clients selon les critères
  const filteredClients = clients.filter(client => {
    // Filtre par recherche
    const matchesSearch = searchTermClients === '' ||
      client.firstName?.toLowerCase().includes(searchTermClients.toLowerCase()) ||
      client.lastName?.toLowerCase().includes(searchTermClients.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchTermClients.toLowerCase()) ||
      client.phoneNumber?.toLowerCase().includes(searchTermClients.toLowerCase()) ||
      (() => {
        // Gérer les différents formats d'adresse pour la recherche
        const adresse = client.address;

        // Si c'est un objet, formater l'adresse pour la recherche
        if (typeof adresse === 'object' && adresse !== null) {
          const { street, city, postalCode, country } = adresse;
          const parties = [];
          if (street) parties.push(street);
          if (city) parties.push(city);
          if (postalCode) parties.push(postalCode);
          if (country) parties.push(country);
          const adresseFormatee = parties.join(' ').toLowerCase();
          return adresseFormatee.includes(searchTermClients.toLowerCase());
        }

        // Si c'est une chaîne, rechercher directement
        if (typeof adresse === 'string') {
          return adresse.toLowerCase().includes(searchTermClients.toLowerCase());
        }

        return false;
      })();

    // Filtre par statut - Support ancien et nouveau système
    const matchesStatus = (() => {
      const filterStatus = advancedFiltersClients.status;

      // Tous les statuts
      if (filterStatus === 'all') return true;

      // Ancien système (basé sur isActive)
      if (filterStatus === 'active') {
        // Actif ancien système = isActive !== false ET pas de nouveau statut (ou statut = actif)
        return client.isActive !== false && (!client.status || client.status === BENEFICIARY_STATUS.ACTIVE);
      }
      if (filterStatus === 'inactive') {
        // Inactif ancien système = isActive === false ET pas de nouveau statut
        return client.isActive === false && !client.status;
      }

      // Nouveau système (basé sur le champ status)
      if (filterStatus === BENEFICIARY_STATUS.ACTIVE) {
        return client.status === BENEFICIARY_STATUS.ACTIVE || (!client.status && client.isActive !== false);
      }
      if (filterStatus === BENEFICIARY_STATUS.PENDING) {
        return client.status === BENEFICIARY_STATUS.PENDING;
      }
      if (filterStatus === BENEFICIARY_STATUS.SUSPENDED) {
        return client.status === BENEFICIARY_STATUS.SUSPENDED;
      }
      if (filterStatus === BENEFICIARY_STATUS.INACTIVE) {
        return client.status === BENEFICIARY_STATUS.INACTIVE;
      }
      if (filterStatus === BENEFICIARY_STATUS.ARCHIVED) {
        return client.status === BENEFICIARY_STATUS.ARCHIVED || (client.isArchived === true);
      }

      return false;
    })();

    // Filtre par type d'entité
    const matchesType = advancedFiltersClients.typeEntite === 'all' ||
      client.typeEntite === advancedFiltersClients.typeEntite;

    // Filtre par présence d'email
    const matchesEmail = advancedFiltersClients.hasEmail === 'all' ||
      (advancedFiltersClients.hasEmail === 'yes' && client.email && client.email.trim() !== '') ||
      (advancedFiltersClients.hasEmail === 'no' && (!client.email || client.email.trim() === ''));

    // Filtre par présence de téléphone
    const matchesPhone = advancedFiltersClients.hasPhone === 'all' ||
      (advancedFiltersClients.hasPhone === 'yes' && client.phoneNumber && client.phoneNumber.trim() !== '') ||
      (advancedFiltersClients.hasPhone === 'no' && (!client.phoneNumber || client.phoneNumber.trim() === ''));

    // Filtre par accès client
    const matchesAuthAccess = advancedFiltersClients.hasAuthAccess === 'all' ||
      (advancedFiltersClients.hasAuthAccess === 'yes' && client.hasAuthAccess === true) ||
      (advancedFiltersClients.hasAuthAccess === 'no' && client.hasAuthAccess !== true);

    return matchesSearch && matchesStatus && matchesType && matchesEmail && matchesPhone && matchesAuthAccess;
  });


  // État pour le client sélectionné pour les documents légaux
  const [selectedClientForDocuments, setSelectedClientForDocuments] = useState(null);

  // État pour le client sélectionné pour les contrats
  const [selectedClientForContracts, setSelectedClientForContracts] = useState(null);

  // Rendu du module actif
  const renderModule = () => {
    if (isLoading || permissionsLoading) {
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
          <div className="text-center">
            <RefreshCw size={32} className="animate-spin mx-auto mb-4 text-blue-600 dark:text-blue-400" />
            <p className="text-gray-600 dark:text-slate-400">Chargement des données...</p>
          </div>
        </div>
      );
    }

    switch (moduleActif) {
      case 'tableau-de-bord':
        return (
          <TableauDeBordModule
            stats={stats}
            employees={employees}
            clients={clients}
            appointments={appointments}
          />
        );
      case 'planning':
        return (
          <PlanningIntervenantsModule
            db={db}
            employees={employees}
            clients={clients}
            appointments={appointments}
            roles={roles}
            currentUser={currentUser}
            onAddAppointment={() => {
              // Action pour ajouter un nouveau rendez-vous
              alert('Fonctionnalité d\'ajout de rendez-vous à implémenter');
            }}
          />
        );
      case 'intervenants':
        return (
          <IntervenantsModule
            filteredEmployees={filteredEmployees}
            employees={employees}
            roles={roles}
            searchTerm={searchTerm}
            showAdvancedFilters={showAdvancedFilters}
            advancedFilters={advancedFilters}
            handleSearchChange={handleSearchChange}
            toggleAdvancedFilters={toggleAdvancedFilters}
            handleAdvancedFiltersChange={handleAdvancedFiltersChange}
            getRoleName={getRoleName}
            hasPermission={hasPermission}
            appointments={appointments}
            onlineStatuses={onlineStatuses}
            onAction={handleResourceAction}
            db={db}
            currentUser={currentUser}
          />
        );
      case 'beneficiaires':
        return (
          <BeneficiairesModule
            filteredClients={filteredClients}
            clients={clients}
            searchTermClients={searchTermClients}
            showAdvancedFiltersClients={showAdvancedFiltersClients}
            advancedFiltersClients={advancedFiltersClients}
            handleSearchClientsChange={handleSearchClientsChange}
            toggleAdvancedFiltersClients={toggleAdvancedFiltersClients}
            handleAdvancedFiltersClientsChange={handleAdvancedFiltersClientsChange}
            hasPermission={hasPermission}
            appointments={appointments}
            employees={employees}
            roles={roles}
            getRoleName={getRoleName}
            onAction={handleResourceAction}
            onAddClient={() => setShowAddClientModal(true)}
            onSelectClientForDocuments={(client) => {
              setSelectedClientForDocuments(client);
              setModuleActif('documents-legaux');
            }}
            db={db}
            currentUser={currentUser}
            contracts={contracts}
          />
        );
      case 'documents-legaux':
        return (
          <LegalDocumentsManager
            client={selectedClientForDocuments}
            user={currentUser}
            onClientSelect={(client) => setSelectedClientForDocuments(client)}
          />
        );
      case 'contrats-clients':
        return (
          <ClientContract
            client={selectedClientForContracts}
            db={db}
            currentUser={currentUser}
          />
        );
      case 'documents-generiques':
        return (
          <GenericDocumentsManager
            user={currentUser}
            db={db}
          />
        );
      default:
        return (
          <TableauDeBordModule
            stats={stats}
            employees={employees}
            clients={clients}
            appointments={appointments}
          />
        );
    }
  };



  // Vérifier les permissions seulement après le chargement
  // Temporairement désactivé pour le débogage
  // if (!hasPermission({ resource: 'resources', action: 'read' })) {
  //   return (
  //     <div className="min-h-screen bg-gray-50 flex items-center justify-center">
  //       <div className="text-center max-w-md mx-auto p-6">
  //         <AlertTriangle size={48} className="mx-auto mb-4 text-red-600" />
  //         <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès refusé</h2>
  //         <p className="text-gray-600">
  //           Vous n'avez pas les permissions nécessaires pour accéder à ce module.
  //         </p>
  //       </div>
  //     </div>
  //   );
  // }



  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header mobile */}
      <div className="md:hidden bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center">
            <button
              onClick={() => setMenuMobileOuvert(!menuMobileOuvert)}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 mr-3 text-gray-600 dark:text-gray-300"
              aria-label="Ouvrir le menu"
            >
              <Menu size={20} />
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">Ressources</h1>
              <p className="text-xs text-gray-600 dark:text-gray-400">Gestion complète des ressources</p>
            </div>
          </div>
          <div className="text-gray-400 dark:text-gray-500">
            {(() => { const m = modules.find(m => m.id === moduleActif); return m ? <m.icone size={20} /> : null; })()}
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Navigation latérale - Desktop */}
        <div className="hidden md:block w-64 bg-white dark:bg-gray-800 shadow-sm min-h-screen">
          <div className="p-6 border-b dark:border-gray-700">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Ressources</h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Gestion complète des ressources</p>
          </div>

          <nav className="p-4">
            <ul className="space-y-1">
              {modules.map((module) => {
                const isActive = moduleActif === module.id;
                const IconComponent = module.icone;
                return (
                  <li key={module.id}>
                    <button
                      onClick={() => setModuleActif(module.id)}
                      className={`w-full text-left p-3 rounded-xl transition-all min-h-[60px] ${
                        isActive
                          ? 'bg-gradient-to-r from-blue-50 to-blue-50/30 dark:from-blue-900/30 dark:to-transparent border-l-4 border-blue-500 dark:border-blue-400 text-blue-700 dark:text-blue-300'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/60 border-l-4 border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <IconComponent
                          size={18}
                          className={isActive ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}
                        />
                        <div>
                          <div className={`font-medium text-sm ${isActive ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'}`}>
                            {module.label}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{module.description}</div>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Informations système */}
          <div className="p-4 border-t dark:border-gray-700 mt-4">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              <p className="font-semibold text-gray-700 dark:text-gray-300">{currentUser?.organizationName || currentUser?.company || 'Agentime'}</p>
              <p className="mt-1">Gestion des ressources</p>
            </div>
          </div>
        </div>

        {/* Navigation mobile - Overlay */}
        {menuMobileOuvert && (
          <div className="md:hidden fixed inset-0 z-50 bg-black bg-opacity-50 dark:bg-opacity-70">
            <div className="fixed inset-y-0 left-0 w-64 bg-white dark:bg-gray-800 shadow-lg">
              <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Navigation</h2>
                <button
                  onClick={() => setMenuMobileOuvert(false)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                  aria-label="Fermer le menu"
                >
                  <X size={20} />
                </button>
              </div>

              <nav className="p-4">
                <ul className="space-y-1">
                  {modules.map((module) => {
                    const isActive = moduleActif === module.id;
                    const IconComponent = module.icone;
                    return (
                      <li key={module.id}>
                        <button
                          onClick={() => {
                            setModuleActif(module.id);
                            setMenuMobileOuvert(false);
                          }}
                          className={`w-full text-left p-3 rounded-xl transition-all min-h-[60px] ${
                            isActive
                              ? 'bg-gradient-to-r from-blue-50 to-blue-50/30 dark:from-blue-900/30 dark:to-transparent border-l-4 border-blue-500 dark:border-blue-400'
                              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/60 border-l-4 border-transparent'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <IconComponent
                              size={18}
                              className={isActive ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}
                            />
                            <div>
                              <div className={`font-medium ${isActive ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'}`}>
                                {module.label}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{module.description}</div>
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>
          </div>
        )}

        {/* Contenu principal */}
        <div className="flex-1 md:ml-0">
          {renderModule()}

          {/* Modals */}
          {showViewModal && selectedResource && (
            resourceType === 'intervenant' ? (
              <ViewEmployeeModal
                employee={selectedResource}
                appointments={appointments}
                clients={clients}
                roles={roles}
                onClose={() => {
                  setShowViewModal(false);
                  setSelectedResource(null);
                  setResourceType(null);
                }}
                onEdit={() => {
                  setShowViewModal(false);
                  setSelectedResource(selectedResource);
                  setResourceType('intervenant');
                  setShowEditModal(true);
                }}
              />
            ) : (
              <ViewClientModal
                client={selectedResource}
                appointments={appointments}
                employees={employees}
                onClose={() => {
                  setShowViewModal(false);
                  setSelectedResource(null);
                  setResourceType(null);
                }}
                onEdit={() => {
                  setShowViewModal(false);
                  setSelectedResource(selectedResource);
                  setResourceType('beneficiaire');
                  setShowEditModal(true);
                }}
              />
            )
          )}

          {showEditModal && selectedResource && (
            resourceType === 'intervenant' ? (
              <EditEmployeeModal
                employee={selectedResource}
                db={db}
                roles={roles}
                onClose={() => {
                  setShowEditModal(false);
                  setSelectedResource(null);
                  setResourceType(null);
                }}
              />
            ) : (
              <EditClientModal
                client={selectedResource}
                db={db}
                onClose={() => {
                  setShowEditModal(false);
                  setSelectedResource(null);
                  setResourceType(null);
                }}
              />
            )
          )}

          {/* Modal NFC */}
          {showNFCModal && selectedResourceForNFC && (
            (() => {
              console.log('Rendering NFC Modal - showNFCModal:', showNFCModal, 'selectedResourceForNFC:', selectedResourceForNFC);
              return (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                  onClick={handleBackdropClick}
                >
                  <div className="relative z-60">
                    <NFCModal
                      client={selectedResourceForNFC}
                      onSubmit={async (nfcData) => {
                        try {
                          await updateDoc(doc(db, "clients", selectedResourceForNFC.id), {
                            nfcTag: nfcData,
                            tagSerialNumber: nfcData.tagSerialNumber
                          });
                          console.log("Tag NFC configuré pour le bénéficiaire:", selectedResourceForNFC.id);
                          setShowNFCModal(false);
                          setSelectedResourceForNFC(null);
                        } catch (error) {
                          console.error("Erreur lors de la configuration du tag NFC:", error);
                        }
                      }}
                      onClose={() => {
                        setShowNFCModal(false);
                        setSelectedResourceForNFC(null);
                      }}
                    />
                  </div>
                </div>
              );
            })()
          )}

          {/* Modal ClientAuthManager */}
          {showAuthModal && selectedResourceForAuth && (
            (() => {
              console.log('Rendering ClientAuthManager Modal - showAuthModal:', showAuthModal, 'selectedResourceForAuth:', selectedResourceForAuth);
              return (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                  onClick={handleBackdropClick}
                >
                  <div className="relative z-60">
                    <ClientAuthManager
                      client={selectedResourceForAuth}
                      db={db}
                      onUpdate={() => {
                        console.log("Accès client mis à jour pour:", selectedResourceForAuth.id);
                        setShowAuthModal(false);
                        setSelectedResourceForAuth(null);
                      }}
                      onClose={() => {
                        setShowAuthModal(false);
                        setSelectedResourceForAuth(null);
                      }}
                    />
                  </div>
                </div>
              );
            })()
          )}

          {/* Modal AddClientModal */}
          {showAddClientModal && (
            (() => {
              console.log('Rendering AddClientModal - showAddClientModal:', showAddClientModal);
              return (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                  onClick={handleBackdropClick}
                >
                  <div className="relative z-60">
                    <AddClientModal
                      db={db}
                      onClose={() => {
                        setShowAddClientModal(false);
                      }}
                      onSuccess={() => {
                        console.log("Nouveau bénéficiaire créé avec succès");
                        setShowAddClientModal(false);
                      }}
                    />
                  </div>
                </div>
              );
            })()
          )}

          {/* Modal InvoiceGeneratorModal */}
          {showInvoiceModal && selectedClientForInvoice && (
            <InvoiceGeneratorModal
              client={selectedClientForInvoice}
              onClose={() => {
                setShowInvoiceModal(false);
                setSelectedClientForInvoice(null);
              }}
              onSuccess={(result) => {
                console.log("Facture générée avec succès:", result);
                // Optionnel: rafraîchir les données si nécessaire
              }}
            />
          )}

          {/* Modal ArchiveBeneficiaryModal */}
          {showArchiveModal && selectedResourceForArchive && (
            (() => {
              const isIntervenant = selectedResourceForArchive.type === 'intervenant';
              console.log(`Rendering Archive${isIntervenant ? 'Intervenant' : 'Beneficiary'}Modal - showArchiveModal:`, showArchiveModal, 'selectedResourceForArchive:', selectedResourceForArchive);

              return (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                  onClick={handleBackdropClick}
                >
                  <div className="relative z-60">
                    {isIntervenant ? (
                      <ArchiveIntervenantModal
                        intervenant={selectedResourceForArchive}
                        db={db}
                        onClose={() => {
                          setShowArchiveModal(false);
                          setSelectedResourceForArchive(null);
                        }}
                        onSuccess={(result) => {
                          console.log("Intervenant archivé avec succès:", result);
                          setShowArchiveModal(false);
                          setSelectedResourceForArchive(null);
                          alert(`${result.intervenantName} a été archivé avec succès${result.appointmentsFreed ? ' et les rendez-vous ont été libérés' : ''}`);
                        }}
                      />
                    ) : (
                      <ArchiveBeneficiaryModal
                        client={selectedResourceForArchive}
                        db={db}
                        onClose={() => {
                          setShowArchiveModal(false);
                          setSelectedResourceForArchive(null);
                        }}
                        onSuccess={(result) => {
                          console.log("Bénéficiaire archivé avec succès:", result);
                          setShowArchiveModal(false);
                          setSelectedResourceForArchive(null);
                          alert(`${result.clientName} a été archivé avec succès${result.appointmentsDeleted ? ' et les rendez-vous ont été supprimés' : ''}`);
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })()
          )}

          {/* Modal ClientStatisticsModal */}
          {showStatisticsModal && selectedClientForStatistics && (
            <ClientStatisticsModal
              client={selectedClientForStatistics}
              db={db}
              onClose={() => {
                setShowStatisticsModal(false);
                setSelectedClientForStatistics(null);
              }}
            />
          )}

          {/* Modal ClientDocumentsPanel pour Contrat de Mandat */}
          {showMandateModal && selectedClientForMandate && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
              onClick={handleBackdropClick}
            >
              <div
                className="relative z-60 bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto m-4"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Documents - {selectedClientForMandate.firstName} {selectedClientForMandate.lastName}
                  </h2>
                  <button
                    onClick={() => {
                      setShowMandateModal(false);
                      setSelectedClientForMandate(null);
                    }}
                    className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    aria-label="Fermer"
                  >
                    <span className="text-xl dark:text-gray-300">✕</span>
                  </button>
                </div>
                <div className="p-6">
                  <ClientDocumentsPanel
                    clientId={selectedClientForMandate.id}
                    clientData={selectedClientForMandate}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Planning Intervenant */}
      {showEmployeeAgendaModal && selectedEmployeeForAgenda && (
        <EmployeeAgendaView
          employee={selectedEmployeeForAgenda}
          appointments={appointments}
          clients={clients}
          db={db}
          onClose={() => {
            setShowEmployeeAgendaModal(false);
            setSelectedEmployeeForAgenda(null);
          }}
          hasPermission={hasPermission}
        />
      )}
    </div>
  );
};

export default ResourceManagementEnhanced;
